import { useState, useEffect } from "react";

interface RawPackageData {
  id: string;
  name: string;
  price: number;
  randevu: string;
}

interface PackageDTO {
  id: string;
  name: string;
  price: number;
  appointment: string;
}

interface User {
  studentName: string;
  gradeName: string;
  appName: string;
}

interface ApiResponse {
  packages?: RawPackageData[];
  studentName?: string;
  gradeName?: string;
  appName?: string;
}

interface UsePackagesReturn {
  packages: PackageDTO[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  uuid: string | null;
  user: User;
}

const mapToPackageDTO = (rawPackage: RawPackageData): PackageDTO => {
  const { randevu, ...rest } = rawPackage;
  return {
    ...rest,
    appointment: randevu,
  };
};

const usePackages = (): UsePackagesReturn => {
  const [packages, setPackages] = useState<PackageDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [uuid, setUuid] = useState<string | null>(null);
  const [user, setUser] = useState<User>({
    studentName: "",
    gradeName: "",
    appName: "",
  });

  const getUrlParams = (): { uuid: string | null } => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      uuid: urlParams.get("data"),
    };
  };

  const fetchPackages = async (): Promise<void> => {
    const { uuid: urlUuid } = getUrlParams();

    setLoading(true);
    setError(null);
    setUuid(urlUuid);

    try {
      const response = await fetch(
        `${process.env.PACKAGES_API}&operation=addon&uuid=${urlUuid}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApiResponse = await response.json();

      const transformedPackages: PackageDTO[] = (data.packages || []).map(
        mapToPackageDTO,
      );

      setPackages(transformedPackages);
      setUser({
        studentName: data?.studentName || "",
        gradeName: data?.gradeName || "",
        appName: data?.appName || "",
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred";
      setError(errorMessage);
      console.error("Payment packages fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const refetch = (): void => {
    fetchPackages();
  };

  return {
    packages,
    loading,
    error,
    refetch,
    uuid,
    user,
  };
};

export default usePackages;
